import React, { useEffect, useState } from "react";
import { PrintButton } from "../components/PrintButton";
import { getRooms, createRoom, updateRoom, deleteRoom } from "../lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { MonitorPlay, ChevronRight, Plus, Trash2, Save, X } from "lucide-react";

const STATUS = {
  registered: { label: "Registered", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  offline: { label: "Offline", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
  issue: { label: "Issue", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  pending: { label: "Pending", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
};
const STATUSES = Object.keys(STATUS);
const PLATFORMS = ["Webex", "Microsoft Teams", "Dual (Webex + Teams)", "Unregistered"];
const MODELS = ["Room Bar", "Room Bar Pro", "Room Kit", "Room Kit Pro", "Board Pro", "Desk Pro", "Room Navigator", "Other"];
const PLAT_CLS = {
  "Webex": "text-emerald-400", "Microsoft Teams": "text-blue-400",
  "Dual (Webex + Teams)": "text-purple-400", "Unregistered": "text-zinc-500",
};

export default function Rooms() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState({ name: "", model: "Room Bar", platform: "Webex", location: "", status: "pending" });

  const load = () => getRooms().then((d) => { setRows(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const startEdit = (r) => { setEditId(r.id); setForm({ ...r }); };
  const saveEdit = async () => {
    try {
      const u = await updateRoom(editId, { name: form.name, model: form.model, platform: form.platform, location: form.location || "", status: form.status });
      setRows((p) => p.map((r) => (r.id === editId ? u : r)));
      setEditId(null);
      toast.success("Room updated");
    } catch { toast.error("Update failed"); }
  };
  const add = async () => {
    if (!newRow.name.trim()) { toast.error("Enter a room name"); return; }
    try {
      const c = await createRoom(newRow);
      setRows((p) => [...p, c]);
      setAdding(false);
      setNewRow({ name: "", model: "Room Bar", platform: "Webex", location: "", status: "pending" });
      toast.success("Room added");
    } catch { toast.error("Add failed"); }
  };
  const remove = async (id) => {
    try { await deleteRoom(id); setRows((p) => p.filter((r) => r.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const total = rows.length;
  const registered = rows.filter((r) => r.status === "registered").length;
  const down = rows.filter((r) => ["offline", "issue"].includes(r.status)).length;
  const teams = rows.filter((r) => r.platform.includes("Teams")).length;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> rooms <ChevronRight className="h-3 w-3" /> collaboration
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-6xl">
        <div className="no-print flex justify-end mb-4"><PrintButton /></div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <MonitorPlay className="h-4 w-4" /> Room Collaboration Tracker
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Conference Rooms</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Inventory every video endpoint — Cisco Room Bar, Room Kit, Board and Desk devices — with its registration
          platform (Cisco Webex, Microsoft Teams or dual) and live status across all sites.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <Stat label="Total rooms" value={total} testId="room-stat-total" />
          <Stat label="Registered" value={registered} testId="room-stat-registered" tone={registered > 0 ? "green" : "default"} />
          <Stat label="Offline / Issue" value={down} testId="room-stat-down" tone={down > 0 ? "red" : "default"} />
          <Stat label="On Teams" value={teams} testId="room-stat-teams" />
        </div>

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.4fr_1fr_1.4fr_1.2fr_0.9fr_auto] gap-3 px-4 py-2.5 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            <span>Room</span><span>Device</span><span>Platform</span><span>Location</span><span>Status</span><span></span>
          </div>

          {loading ? (
            <div className="p-6 text-zinc-500 font-mono text-sm">Loading rooms…</div>
          ) : (
            <div className="divide-y divide-zinc-800" data-testid="rooms-list">
              {rows.map((r) => {
                const editing = editId === r.id;
                return (
                  <div key={r.id} data-testid={`room-row-${r.id}`} className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1.4fr_1.2fr_0.9fr_auto] gap-3 px-4 py-3 items-center">
                    {editing ? (
                      <>
                        <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Select value={form.model} onValueChange={(v) => setForm({ ...form, model: v })}>
                          <SelectTrigger data-testid={`edit-room-model-${r.id}`} className="h-8 bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">{MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                          <SelectTrigger data-testid={`edit-room-platform-${r.id}`} className="h-8 bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Location" className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                          <SelectTrigger data-testid={`edit-room-status-${r.id}`} className="h-8 bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="flex items-center gap-1.5">
                          <button onClick={saveEdit} data-testid={`save-room-${r.id}`} className="h-8 w-8 flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-700 text-white"><Save className="h-4 w-4" /></button>
                          <button onClick={() => setEditId(null)} className="h-8 w-8 flex items-center justify-center rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300"><X className="h-4 w-4" /></button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-white font-medium">{r.name}</div>
                        <div className="text-[13px] text-zinc-300">{r.model}</div>
                        <div className={`text-[12px] font-semibold ${PLAT_CLS[r.platform] || "text-zinc-400"}`}>{r.platform}</div>
                        <div className="text-[13px] text-zinc-400 truncate">{r.location || "—"}</div>
                        <div><span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${STATUS[r.status]?.cls || ""}`}>{STATUS[r.status]?.label || r.status}</span></div>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => startEdit(r)} data-testid={`edit-room-${r.id}`} className="text-[11px] text-blue-400 hover:underline">Edit</button>
                          <button onClick={() => remove(r.id)} data-testid={`delete-room-${r.id}`} className="h-7 w-7 flex items-center justify-center rounded-md text-zinc-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {adding ? (
            <div className="p-4 border-t border-zinc-800 bg-zinc-950/50" data-testid="add-room-form">
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                <Input data-testid="new-room-name" value={newRow.name} onChange={(e) => setNewRow({ ...newRow, name: e.target.value })} placeholder="Room name" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Select value={newRow.model} onValueChange={(v) => setNewRow({ ...newRow, model: v })}>
                  <SelectTrigger data-testid="new-room-model" className="h-9 bg-zinc-900 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">{MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={newRow.platform} onValueChange={(v) => setNewRow({ ...newRow, platform: v })}>
                  <SelectTrigger data-testid="new-room-platform" className="h-9 bg-zinc-900 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={newRow.location} onChange={(e) => setNewRow({ ...newRow, location: e.target.value })} placeholder="Location" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Select value={newRow.status} onValueChange={(v) => setNewRow({ ...newRow, status: v })}>
                  <SelectTrigger className="h-9 bg-zinc-900 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setAdding(false)} className="px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm">Cancel</button>
                <button onClick={add} data-testid="confirm-add-room" className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">Add Room</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} data-testid="add-room-btn" className="w-full flex items-center justify-center gap-2 px-4 py-3 border-t border-zinc-800 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/40 transition-colors">
              <Plus className="h-4 w-4" /> Add room
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testId, tone = "default" }) => {
  const cls = tone === "red" ? "text-red-400" : tone === "amber" ? "text-amber-400" : tone === "green" ? "text-emerald-400" : "text-white";
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid={testId}>
      <div className={`font-heading text-3xl font-extrabold ${cls}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{label}</div>
    </div>
  );
};
